import { Dispatch, useEffect, useState } from 'react';
import errorConfig from '../config.error.json';
import {
  Consent,
  useAlertContext,
  EmploymentVerificationConsent,
  useFeatureContext,
  IConsent,
  ErrorCode,
} from '../common';
import {
  IRequestApiCallStatus,
  IRequestState,
  RequestAction,
  initRequestState,
  requiresEmploymentVerification,
  getConsents,
  getConsentDownloads,
  confirmDeclineConsent,
  getCurrentRequestType,
} from '.';
import { pathOr } from 'rambda';
import { left, right, map as emap, fold } from 'fp-ts/lib/Either';
import { find as lfind, compose as lcompose, tail as ltail } from 'lodash/fp';
import { flow } from 'fp-ts/lib/function';

export function useConfirmSubmitRequest({
  requestState,
  refreshToken,
  dispatchRequest,
}: {
  requestState: IRequestState;
  refreshToken: () => Promise<boolean>;
  dispatchRequest: Dispatch<RequestAction>;
}) {
  const { setAlert } = useAlertContext();

  const [consents, setConsents] = useState<IConsent[]>([]);
  useEffect(() => {
    lcompose(
      setConsents,
      getConsents,
      pathOr(initRequestState.request, 'request')
    )(requestState);
  }, [requestState]);

  const { feature } = useFeatureContext();

  useEffect(() => {
    lcompose(
      emap(() =>
        setAlert(a => ({
          ...a,
          title: 'CSOS Submit Request',
          message: () => (
            <Consent
              consent={consents[0]}
              downloads={lcompose(
                getConsentDownloads,
                pathOr(initRequestState.request, 'request')
              )(requestState)}
              handleClose={
                requestType === 'REVOKE' || requestType === 'RENEWAL'
                  ? revokehandleClose
                  : handleClose
              }
            />
          ),
          okAction: false,
          cancelAction: false,
          isOpen: true,
        }))
      ),
      a => (a === void 0 ? left(void 0) : right(void 0)),
      lfind<IRequestApiCallStatus>({
        type: 'ConfirmSubmitRequest',
        apiCallStatus: null,
      }),
      pathOr<IRequestApiCallStatus[]>([], 'apiCallStatus')
    )(requestState);

    const handleClose = (a: boolean) => {
      setAlert(b => ({ ...b, isOpen: false, isOk: a }));

      lcompose(
        fold<boolean, boolean, void>(
          a =>
            requiresEmploymentVerification(feature.evs)(requestState)
              ? openEmploymentVerificationConsent(a)
              : dispatchRequest({ type: 'ConfirmSubmitRequest', payload: a }),
          a =>
            a
              ? lcompose(setConsents, ltail)(consents)
              : setConsents([confirmDeclineConsent, ...consents])
        ),
        b => ((b >= 2 && a) || (b <= 1 && a === false) ? right(a) : left(a))
      )(consents.length);
    };

    const openEmploymentVerificationConsent = lcompose(
      fold<boolean, boolean, void>(
        (a: boolean) => setAlert(b => ({ ...b, isOpen: false, isOk: a })),

        lcompose((a: boolean) => {
          setAlert(b => ({ ...b, isOpen: false, isOk: a }));

          refreshToken().then(a =>
            a
              ? dispatchRequest({
                  type: 'ConfirmEmploymentVerificationSubmitRequest',
                  payload: a,
                })
              : dispatchRequest({
                  type: 'SaveRequestError',
                  payload: {
                    code: ErrorCode.RefreshTokenError,
                    message: errorConfig.refreshTokenFailed,
                  },
                })
          );
        })
      ),
      (a: boolean) => (a ? right(a) : left(a))
    );

    const requestType = getCurrentRequestType(requestState);

    //RENEWAL uses the same consent as REVOKE
    const revokehandleClose = (a: boolean) => {
      setAlert(b => ({ ...b, isOpen: false, isOk: a }));

      lcompose(
        fold<boolean, void, void>(
          a => dispatchRequest({ type: 'ConfirmSubmitRequest', payload: a }),
          () => lcompose(setConsents, ltail)(consents)
        ),
        b => (b <= 1 || a === false ? left(a) : right(0))
      )(consents.length);
    };
  }, [
    consents,
    dispatchRequest,
    feature.evs,
    refreshToken,
    requestState,
    setAlert,
  ]);

  useEffect(() => {
    lcompose(
      emap(() =>
        setAlert(a => ({
          ...a,
          title: 'CSOS Submit Request',
          message: () => employmentConsent(requestState),
          okAction: false,
          cancelAction: false,
          isOpen: true,
        }))
      ),
      a => (a === void 0 ? left(void 0) : right(void 0)),
      lfind<IRequestApiCallStatus>({
        type: 'ConfirmEmploymentVerification',
        apiCallStatus: null,
      }),
      pathOr<IRequestApiCallStatus[]>([], 'apiCallStatus')
    )(requestState);

    const employmentConsent = lcompose(
      ({ requestId }) => (
        <EmploymentVerificationConsent
          requestId={requestId}
          handleClose={handleEmploymentVerificationClose}
        />
      ),
      pathOr(initRequestState.request, 'request')
    );

    const handleEmploymentVerificationClose = flow(
      () => setAlert(b => ({ ...b, isOpen: false, isOk: false })),
      () => dispatchRequest({ type: 'ConfirmSubmitRequest', payload: false })
    );
  }, [dispatchRequest, requestState, setAlert]);
}
